import * as d3 from 'd3';
import cloneDeep from 'lodash/cloneDeep';

// Relements
import Notification from 'relements/build/esm/overlays/notification';

// Actions
import { saveNodePositions } from 'actions/BotActions';

// Store
import BotStore from '@src/store';

// Constants
import { MIX_PANEL } from '@src/const';
import {
  VISIBLE_NODE_WIDTH,
  VISIBLE_NODE_HEIGHT,
  VISIBLE_NODE_X,
  VISIBLE_NODE_Y,
  CONSTANTS,
} from './constants';
import GraphStore from '../../../stores/GraphStore';

export default class GraphStepsSelection {
  constructor() {
    this.xMinRect = Number.MAX_SAFE_INTEGER;
    this.xMaxRect = Number.MIN_SAFE_INTEGER;
    this.yMinRect = Number.MAX_SAFE_INTEGER;
    this.yMaxRect = Number.MIN_SAFE_INTEGER;

    this.gridContainer = d3.select('#gridcontainer');
    this.element = null;
    this.previousElement = null;
    this.currentY = 0;
    this.currentX = 0;
    this.originY = 0;
    this.originX = 0;

    this.selectedSteps = {};

    this.mixPanelTracker = BotStore.getMixPanel();
    this.isLHSHotkeyEventFired = false;
    this.isGraphHotkeyEventFired = false;
    this.isNotificationActive = false;
    this.isLimitNotificationDisplayed = false;

    d3.select('body').on('keydown', () => {
      if (d3.event.key === 'Escape') {
        this.resetSelectedSteps();
      }
    });
  }

  getSelectedSteps() {
    return this.selectedSteps;
  }

  setSelectedSteps(selectedSteps, callback = () => {}) {
    const isMultipleStepsSelected = Object.keys(selectedSteps)?.length > 1;

    this.selectedSteps = selectedSteps;
    GraphStore.setSelectedSteps(cloneDeep(selectedSteps));

    // Notification
    if (isMultipleStepsSelected && !this.isNotificationActive) {
      Notification.send({
        title: "Press 'esc' to deselect",
        type: 'info',
        closeable: true,
        timeout: 4000,
      });
      this.isNotificationActive = true;
    } else if (!isMultipleStepsSelected) {
      this.isNotificationActive = false;
      Notification.destroyAll();
    }

    callback();
  }

  resetSelectedRectCoordinates() {
    this.xMinRect = Number.MAX_SAFE_INTEGER;
    this.yMinRect = Number.MAX_SAFE_INTEGER;
    this.xMaxRect = Number.MIN_SAFE_INTEGER;
    this.yMaxRect = Number.MIN_SAFE_INTEGER;
  }

  updateSelectedRect(d) {
    d3.select(`#node-${d.stepId}`).attr('transform', (step) => {
      this.xMinRect = this.xMinRect < step.x ? this.xMinRect : step.x;
      this.xMaxRect = this.xMaxRect > step.x ? this.xMaxRect : step.x;
      this.yMinRect = this.yMinRect < step.y ? this.yMinRect : step.y;
      this.yMaxRect = this.yMaxRect > step.y ? this.yMaxRect : step.y;

      return 'translate(' + step.x + ',' + step.y + ')';
    });

    d3.select('#selected-steps-boundary')
      .attr(
        'visibility',
        Object.keys(this.selectedSteps)?.length < 2 ? 'hidden' : 'visible'
      )
      .attr('x', this.xMinRect + VISIBLE_NODE_X)
      .attr('y', this.yMinRect + VISIBLE_NODE_Y)
      .attr(
        'width',
        Math.abs(this.xMaxRect - this.xMinRect) + VISIBLE_NODE_WIDTH + 20
      )
      .attr(
        'height',
        Math.abs(this.yMaxRect - this.yMinRect) + VISIBLE_NODE_HEIGHT + 20
      );
  }

  selectedStepStyling(d, escapeKeyPressed) {
    const borderColor = d.active
      ? d.fillColor || d.color.primary
      : CONSTANTS.node_inactive_color;
    const textColor = d.active ? '#333' : '#aaa';
    const inactiveFillColor = escapeKeyPressed ? 'white' : d.inactiveColor;
    const fillColor = escapeKeyPressed
      ? 'white'
      : d.fillColor || d.color.primary;

    d3.select(`#node-${d.stepId}`)
      .style(
        '--border-color',
        this.selectedSteps?.[d.stepId]
          ? `${CONSTANTS.node_selected_stroke_color}${!d.active ? 54 : ''}`
          : borderColor
      )
      .style('--text-color', textColor);

    if (d.active) {
      d3.select(`#gradient-${d.stepId} > stop:first-child`).attr(
        'stop-color',
        this.selectedSteps?.[d.stepId]
          ? CONSTANTS.node_selected_fill_color
          : fillColor
      );
    } else {
      d3.select(`#gradient-inactive-${d.stepId} > stop:first-child`).attr(
        'stop-color',
        this.selectedSteps?.[d.stepId]
          ? CONSTANTS.node_selected_fill_color
          : inactiveFillColor
      );
    }

    d3.select(`#outer-border-box-${d.stepId}`).attr(
      'visibility',
      this.selectedSteps?.[d.stepId] ? 'visible' : 'hidden'
    );

    this.updateSelectedRect(d);
  }

  updateAllSelectedStepsStyling() {
    Object.keys(this.selectedSteps).map((stepId) => {
      this.selectedStepStyling(this.selectedSteps[stepId]);
    });
  }

  resetSelectedSteps(currentSelectedStep = '') {
    const selectedSteps = this.selectedSteps;
    this.setSelectedSteps({}, () => {
      Object.keys(selectedSteps).map((stepId) => {
        const isConnected =
          currentSelectedStep &&
          (currentSelectedStep.destinationList.includes(stepId) ||
            currentSelectedStep.sourceList.includes(stepId));
        this.selectedStepStyling(selectedSteps[stepId], !isConnected);
      });
    });
    this.resetSelectedRectCoordinates();
    this.isLHSHotkeyEventFired = false;
    this.isGraphHotkeyEventFired = false;
  }

  isLimitReached() {
    if (Object.keys(this.selectedSteps).length === 100) {
      if (!this.isLimitNotificationDisplayed) {
        Notification.destroyAll();
        Notification.send({
          title: 'You can select maximum 100 steps at a time',
          type: 'info',
          closeable: true,
          timeout: 4000,
        });
        this.isLimitNotificationDisplayed = true;
      }
      return true;
    }
    return false;
  }

  onStepClick(
    keyPressed,
    d,
    callback = () => {},
    isLHSNavigationClick = false,
    isSkillNavigationClick = false
  ) {
    let stepData = d;
    if (!stepData.stepId) {
      stepData = {
        ...stepData,
        stepId: stepData.id,
      };
    }
    if (keyPressed) {
      let currentSelectedSteps = [];
      let isAlreadyPresent = false;

      if (
        stepData &&
        !this.selectedSteps?.[stepData.stepId] &&
        this.isLimitReached()
      ) {
        if (!isSkillNavigationClick) this.isLimitNotificationDisplayed = false;
        return;
      }

      if (this.selectedSteps?.[stepData.stepId]) {
        currentSelectedSteps = this.selectedSteps;
        delete currentSelectedSteps[stepData.stepId];
        isAlreadyPresent = true;
      } else {
        currentSelectedSteps = {
          ...this.selectedSteps,
          [stepData.stepId]: stepData,
        };
      }

      this.setSelectedSteps(currentSelectedSteps, () => {
        this.selectedStepStyling(stepData, isLHSNavigationClick);
        if (isAlreadyPresent) {
          // Update the boundary of selected steps area
          this.resetSelectedRectCoordinates();
          Object.keys(this.selectedSteps).map((stepId) => {
            this.updateSelectedRect(this.selectedSteps[stepId]);
          });
        }
      });

      if (isLHSNavigationClick && !this.isLHSHotkeyEventFired) {
        this.isLHSHotkeyEventFired = true;
        this.mixPanelTracker.track({
          event: MIX_PANEL.GRAPH_MULTIPLE_STEPS.SELECTION,
          payload: {
            Source: 'LHS Hotkey',
          },
        });
      } else if (!this.isGraphHotkeyEventFired) {
        this.isGraphHotkeyEventFired = true;
        this.mixPanelTracker.track({
          event: MIX_PANEL.GRAPH_MULTIPLE_STEPS.SELECTION,
          payload: {
            Source: 'Graph Hotkey',
          },
        });
      }
    } else if (
      !(d3.event?.shiftKey || d3.event?.altKey) &&
      !this.selectedSteps?.[stepData.stepId]
    ) {
      this.resetSelectedSteps();
      this.setSelectedSteps(
        {
          [stepData.stepId]: stepData,
        },
        () => this.selectedStepStyling(stepData)
      );
    }

    callback();
  }

  onDrag(d, changeInX, changeInY) {
    const isInSelectedSteps =
      (
        Object.keys(this.selectedSteps).filter(
          (stepId) => stepId === d.stepId
        ) || []
      )?.length === 1;
    if (
      !isInSelectedSteps &&
      !(d3.event.sourceEvent.metaKey || d3.event.sourceEvent.ctrlKey)
    ) {
      this.resetSelectedSteps(d);
      this.setSelectedSteps(
        {
          [d.stepId]: d,
        },
        () => this.selectedStepStyling(d)
      );
    }
    this.resetSelectedRectCoordinates();
    Object.keys(this.selectedSteps).map((stepId) => {
      if (stepId !== d.stepId) {
        d3.select(`#node-${stepId}`).attr('transform', (step) => {
          step.x = step.x + changeInX;
          step.y = step.y + changeInY;

          this.selectedSteps[stepId].x = step.x;
          this.selectedSteps[stepId].y = step.y;

          return 'translate(' + step.x + ',' + step.y + ')';
        });
      }

      this.updateSelectedRect(this.selectedSteps[stepId]);
    });
  }

  onDragEnd(d, draggedPos, setNodePosition, changeInX, changeInY) {
    this.resetSelectedRectCoordinates();

    Object.keys(this.selectedSteps).map((stepId) => {
      if (stepId !== d.stepId) {
        setNodePosition(
          stepId,
          this.selectedSteps[stepId].x + changeInX,
          this.selectedSteps[stepId].y + changeInY,
          this.selectedSteps[stepId].originalX,
          this.selectedSteps[stepId].originalY
        );

        d3.select(`#node-${stepId}`).attr('transform', (step) => {
          step.x = step.x + changeInX;
          step.y = step.y + changeInY;

          return 'translate(' + step.x + ',' + step.y + ')';
        });
      }

      this.updateSelectedRect(this.selectedSteps[stepId]);
    });

    if (d.originalX - draggedPos.x === 0 && d.originalY - draggedPos.y === 0) {
      return;
    }

    Object.keys(this.selectedSteps).map((stepId) => {
      if (stepId !== d.stepId) {
        saveNodePositions(
          stepId,
          this.selectedSteps[stepId].type,
          this.selectedSteps[stepId].x + changeInX,
          this.selectedSteps[stepId].y + changeInY
        );
      }
    });

    this.mixPanelTracker.track({
      event: MIX_PANEL.GRAPH_MULTIPLE_STEPS.DRAG,
    });
  }

  setElement(ele) {
    this.previousElement = this.element;
    this.element = ele;
  }

  getNewAttributes() {
    const x = this.currentX < this.originX ? this.currentX : this.originX;
    const y = this.currentY < this.originY ? this.currentY : this.originY;
    const width = Math.abs(this.currentX - this.originX);
    const height = Math.abs(this.currentY - this.originY);
    return {
      x,
      y,
      width,
      height,
    };
  }

  getCurrentAttributes() {
    const x = +this.element.attr('x');
    const y = +this.element.attr('y');
    const width = Math.abs(this.originX - this.currentX);
    const height = Math.abs(this.originY - this.currentY);
    return {
      x1: x,
      y1: y,
      x2: x + width,
      y2: y + height,
    };
  }

  update(newX, newY) {
    this.currentX = newX;
    this.currentY = newY;
    const newAttributes = this.getNewAttributes();
    this.element
      .attr('x', newAttributes.x)
      .attr('y', newAttributes.y)
      .attr('width', newAttributes.width)
      .attr('height', newAttributes.height);
  }

  init(newX, newY, gridContainer) {
    const rectElement = gridContainer
      .append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', 0)
      .attr('height', 0)
      .classed('selection', true);
    this.setElement(rectElement);
    this.originX = newX;
    this.originY = newY;
    this.update(newX, newY);
  }

  remove() {
    this.element.remove();
    this.element = null;
  }

  removePrevious() {
    if (this.previousElement) {
      this.previousElement.remove();
    }
    if (
      !(d3.event.sourceEvent.metaKey || d3.event.sourceEvent.ctrlKey) &&
      this.element &&
      Object.keys(this.selectedSteps)?.length
    ) {
      const selectedSteps = this.selectedSteps;
      Object.values(selectedSteps).map((step) => {
        this.selectedStepStyling({ ...step, stepId: step.id });
      });
      this.setSelectedSteps({});
    }
  }

  dragStarted(p, gridContainer) {
    this.init(p[0], p[1], gridContainer);
    this.removePrevious();
  }

  dragged(p) {
    this.update(p[0], p[1]);
  }

  dragEnded(posMap, idMap, isInactiveNodesVisible, callback) {
    const finalAttributes = this.getCurrentAttributes();
    if (
      finalAttributes.x2 - finalAttributes.x1 > 1 &&
      finalAttributes.y2 - finalAttributes.y1 > 1
    ) {
      d3.event.sourceEvent.preventDefault();
      const minX = this.originX < this.currentX ? this.originX : this.currentX;
      const maxX = this.originX > this.currentX ? this.originX : this.currentX;
      const minY = this.originY < this.currentY ? this.originY : this.currentY;
      const maxY = this.originY > this.currentY ? this.originY : this.currentY;
      Object.keys(posMap).forEach((xCoordinate) => {
        if (xCoordinate >= minX && xCoordinate <= maxX) {
          Object.keys(posMap[xCoordinate]).forEach((yCoordinate) => {
            const stepData = idMap[posMap[xCoordinate][yCoordinate]];
            if (
              stepData &&
              !this.selectedSteps?.[stepData.stepId] &&
              this.isLimitReached()
            ) {
              return;
            }
            if (
              stepData &&
              yCoordinate >= minY &&
              yCoordinate <= maxY &&
              stepData.subStoryActive &&
              (stepData.active || isInactiveNodesVisible)
            ) {
              this.setSelectedSteps(
                {
                  ...this.selectedSteps,
                  [posMap[xCoordinate][yCoordinate]]: {
                    ...stepData,
                  },
                },
                () => {
                  this.selectedStepStyling(stepData);
                  if (Object.keys(this.selectedSteps).length > 1) {
                    callback(stepData);
                  }
                }
              );
            }
          });
        }
      });
      this.isLimitNotificationDisplayed = false;
    }
    this.remove();
    this.mixPanelTracker.track({
      event: MIX_PANEL.GRAPH_MULTIPLE_STEPS.SELECTION,
      payload: {
        Source: 'Range',
      },
    });
  }

  setIsLimitNotificationDisplayed(value) {
    this.isLimitNotificationDisplayed = value;
  }
}
